import { TokenStorage } from './token-storage';

interface FCMTokenData {
  token: string;
  deviceInfo: {
    userAgent: string;
    platform: string;
    language: string;
    deviceId?: string;
  };
}

// Track ongoing requests to prevent duplicate registrations
const ongoingRequests = new Map<string, Promise<boolean>>();
// Track successfully registered tokens to prevent re-registration
const registeredTokens = new Set<string>();

export async function sendFCMTokenToBackend(token: string): Promise<boolean> {
  // Check if token is already registered
  if (registeredTokens.has(token)) {
    console.log('FCM token already registered, skipping...');
    return true;
  }

  // Check if there's already an ongoing request for this token
  if (ongoingRequests.has(token)) {
    console.log('FCM token registration already in progress, waiting...');
    return ongoingRequests.get(token)!;
  }

  const requestPromise = performFCMTokenRegistration(token);
  ongoingRequests.set(token, requestPromise);

  try {
    const result = await requestPromise;
    if (result) {
      registeredTokens.add(token);
    }
    return result;
  } finally {
    // Clean up the ongoing request
    ongoingRequests.delete(token);
  }
}

async function performFCMTokenRegistration(token: string): Promise<boolean> {
  try {
    const authToken = TokenStorage.getAccessToken();
    if (!authToken) {
      console.log('No auth token available, skipping FCM token registration');
      return false;
    }

    // Check if token is expired
    if (TokenStorage.isAccessTokenExpired()) {
      console.log('Auth token is expired, skipping FCM token registration');
      return false;
    }

    const deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language
    };

    const tokenData: FCMTokenData = {
      token,
      deviceInfo
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch(`${process.env.REACT_APP_CORE_BACKEND_ENDPOINT || 'http://localhost:8210'}/fcm/register/admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(tokenData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        console.log('FCM token registered successfully');
        return true;
      } else {
        const errorText = await response.text();
        console.error('Failed to register FCM token:', response.status, errorText);
        return false;
      }
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        console.error('FCM token registration timed out');
      } else {
        console.error('Network error during FCM token registration:', fetchError);
      }
      return false;
    }
  } catch (error) {
    console.error('Error registering FCM token:', error);
    return false;
  }
}

export async function removeFCMTokenFromBackend(token: string): Promise<boolean> {
  try {
    const authToken = TokenStorage.getAccessToken();
    if (!authToken) {
      console.log('No auth token available, skipping FCM token unregistration');
      return false;
    }

    const response = await fetch(`${process.env.REACT_APP_CORE_BACKEND_ENDPOINT || 'http://localhost:8210'}/fcm/unregister`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ token })
    });

    if (response.ok) {
      console.log('FCM token unregistered successfully');
      return true;
    } else {
      console.error('Failed to unregister FCM token:', response.statusText);
      return false;
    }
  } catch (error) {
    console.error('Error unregistering FCM token:', error);
    return false;
  }
}

export function storeFCMToken(token: string): void {
  try {
    localStorage.setItem('fcmToken', token);
    console.log('FCM token stored locally');
  } catch (error) {
    console.error('Error storing FCM token:', error);
  }
}

export function getStoredFCMToken(): string | null {
  try {
    return localStorage.getItem('fcmToken');
  } catch (error) {
    console.error('Error retrieving FCM token:', error);
    return null;
  }
}

export function removeStoredFCMToken(): void {
  try {
    localStorage.removeItem('fcmToken');
    console.log('FCM token removed from local storage');
  } catch (error) {
    console.error('Error removing FCM token:', error);
  }
}

export function clearRegisteredTokens(): void {
  registeredTokens.clear();
  console.log('Cleared registered FCM tokens cache');
}
